import VueRouter, {
  type Route, type NavigationGuardNext, RouteConfig,
  RawLocation,
} from 'vue-router';
import { clearOrderCreateStore } from '@/utils/helpers';
import {
  emitWindowEvent, getFromSessionStorage, hasPayload, storeInSessionStorage,
} from '@/apps/order/helper';
import {
  clearIfNew,
  hasCompanySettings,
  hasDataPreparedStepCalculator,
  hasDataPreparedStepAddress,
  hasDataPreparedStepConsumptionPoint,
  hasDataPreparedStepPayment,
  hasCalculatorFormData,
  canAccessStep,
  hasPreparedData,
  canQueryCustomer,
  hasFormData,
  hasCategories,
  hasExistingCustomerData,
  hasFeatures,
  hasDataPreparedStepCustomFields,
  ensureTariffChangeData,
  ensureRelocationData,
  hasRelocationSelectedTariffs,
} from '@/apps/order/routes/guards';
import Meta from '@/helper/Router';
import { $text } from '@/utils/TextHelper';
import { orderTrack } from '@/apps/order/utils/orderTrack';

type GuardRouteFunction = (toRoute: Route, fromRoute: Route | undefined, next: NavigationGuardNext) => Promise<void>;

const runGuards = async (...routerParams: any[]) => {
  const to = routerParams[0] as Route;
  const from = routerParams[1] as Route;
  const next = routerParams[2] as NavigationGuardNext;

  const guards: GuardRouteFunction[] = [];
  routerParams.splice(3).forEach((guardFunc: GuardRouteFunction) => {
    guards.push(guardFunc);
  });

  emitWindowEvent('showLoader');

  await clearIfNew(to);

  // eslint-disable-next-line guard-for-in,no-restricted-syntax
  for (const guardInx in Object.keys(guards)) {
    const guard = guards[guardInx];

    try {
      // eslint-disable-next-line no-await-in-loop
      await guard(to, from, next);
    } catch (e) {
      const error = e as unknown as Error & { params?: Record<string, any> };
      if ((error?.message || '').startsWith('to:')) {
        const routeName = error.message.slice(3, error.message.length + 1);

        return next({
          name: routeName,
          params: error.params || {},
          replace: true,
        });
      }
      console.error(`Error running guard ${guard.name}`, error);

      // for tariff-change redirect to initial route instead of default orders-create route
      if (to.meta?.mode === 'tariff-change' && to.params.contractUuid) {
        const initialRoute = ordersCreateRouter.getRoutes().find((route) => route.name === 'orders-tariff-change');
        if (initialRoute) {
          return next({
            name: initialRoute.name as string,
            params: { contractUuid: to.params.contractUuid },
            replace: true,
          });
        }
      }

      // for relocation redirect to initial route instead of default orders-create route
      if (to.meta?.mode === 'relocation' && to.params.contractUuid) {
        const initialRoute = ordersCreateRouter.getRoutes().find((route) => route.name === 'orders-relocation');
        if (initialRoute) {
          return next({
            name: initialRoute.name as string,
            params: { contractUuid: to.params.contractUuid },
            replace: true,
          });
        }
      }

      return next({ name: from?.name || 'orders-create' });
    }
  }

  return next();
};

const { basePath, isSales, orderMode } = (window.ROUTER || {
  basePath: '/',
  isSales: false,
  orderMode: null,
}) as {
  basePath: string;
  isSales?: boolean;
  orderMode?: string | null;
};

const BASE_PATH = basePath ?? '/';
const getTabSessionKey = (storageKey: string): string => {
  const existing = sessionStorage.getItem(storageKey);
  if (existing) return existing;

  const created = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  sessionStorage.setItem(storageKey, created);
  return created;
};

const normalizeBase = (base: string): string => `/${base || ''}`.replace(/\/{2,}/g, '/').replace(/\/+$/, '') || '/';

const slugify = (value: string): string => value
  .replace(/\/+/g, '__')
  .replace(/^__|__$/g, '')
  .replace(/[^a-zA-Z0-9._-]/g, '_')
  || 'root';

export const buildStablePathSlug = (path: string): string => {
  const base = normalizeBase(path);
  const { pathname } = window.location;

  // Pfad relativ zur Base (damit "/orders" nicht doppelt drin ist)
  const afterBase = pathname.startsWith(base) ? pathname.slice(base.length) : pathname;

  // 1) EDIT "/:uuid/edit/...": use only uuid + "edit"
  const editMatch = afterBase.match(/^\/([^/]+)\/edit(\/|$)/);
  if (editMatch) {
    const orderUuid = editMatch[1];
    return slugify(`${base}/edit/${orderUuid}`);
  }

  // 2) CREATE: "/create/..."
  if (/^\/create(\/|$)/.test(afterBase)) {
    return slugify(`${base}/create`);
  }

  // optional: falls ihr noch andere Einstiege habt
  return slugify(`${base}/root`);
};

// --- CONTEXT bauen ---
const PATH_SLUG = buildStablePathSlug(BASE_PATH);

// SessionKey pro Tab, aber *gebunden an diesen Einstieg*:
const SESSION_KEY = getTabSessionKey(`io.ctx.${PATH_SLUG}`);

export const clearTabSession = () => {
  sessionStorage.removeItem(`io.ctx.${PATH_SLUG}`);
};

export const getTabSessionKeyByPath = (slug: string) => getTabSessionKey(`io.ctx.${slug}`);

const CONTEXT = `${PATH_SLUG}.${SESSION_KEY}`;

const clearCacheOnEnter = async (toRoute: Route, fromRoute: Route | undefined) => {
  if (fromRoute?.name) return;

  await clearOrderCreateStore(CONTEXT);
};

// Reload persistence: if not in window.ROUTER, check IDB
const ORDER_MODE = orderMode || undefined;
// io.center's blade defaults `source` to 'tariffChange' when the query param is absent
// (every step-URL reload); that default must never overwrite a running journey's mode.
if (ORDER_MODE && getFromSessionStorage<string>(`${CONTEXT}.orderMode`) === undefined) {
  storeInSessionStorage(`${CONTEXT}.orderMode`, ORDER_MODE);
}

const getOrderMode = async (): Promise<string | null> => {
  if (ORDER_MODE) {
    return ORDER_MODE;
  }
  const storedMode = getFromSessionStorage<string>(`${CONTEXT}.orderMode`);
  return storedMode || null;
};

const IS_SALES = !!isSales;

// initial route evaluation, the actual mode will be determined in guards/views
const IS_TARIFF_CHANGE = ORDER_MODE === 'tariffChange';

const sharedMeta = {
  ctx: CONTEXT,
  isSales: IS_SALES,
  isTariffChange: IS_TARIFF_CHANGE,
  basePath: BASE_PATH,
};

const routes: RouteConfig[] = [
  {
    name: 'orders-create',
    path: '/create',
    meta: {
      step: 'type',
      title: 'Kunde auswählen',
      description: 'Bitte wählen Sie den Kunden aus, für den Sie einen Auftrag anlegen möchten.',
      mode: 'create',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      clearCacheOnEnter,
      canQueryCustomer,
      hasExistingCustomerData,
      hasDataPreparedStepCustomFields,
    ),
    component: () => import(/* webpackChunkName: "order-start" */'@/apps/order/views/OrderCreateView.vue'),
  },
  {
    name: 'orders-create-customer',
    path: '/create/customer',
    meta: {
      step: 'customer',
      title: 'Kunde auswählen',
      description: 'Bitte wählen Sie den Kunden aus, für den Sie einen Auftrag anlegen möchten.',
      mode: 'create',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      canAccessStep,
      hasFormData,
      hasCompanySettings,
      canQueryCustomer,
      hasExistingCustomerData,
      hasDataPreparedStepCustomFields,
      hasCategories,
    ),
    component: () => import(/* webpackChunkName: "order-customer" */ '@/apps/order/views/OrderCreateCustomerView.vue'),
  },
  {
    name: 'orders-create-category',
    path: '/create/category',
    meta: {
      step: 'category',
      title: 'Produktart auswählen',
      description: 'Bitte wählen Sie eine Produktart aus, für die Sie einen Auftrag anlegen möchten.',
      mode: 'create',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      canAccessStep,
      hasFormData,
      hasExistingCustomerData,
      hasCategories,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepCustomFields,
      hasFeatures,
    ),
    component: () => import(/* webpackChunkName: "order-category" */ '@/apps/order/views/OrderCreateCategoryView.vue'),
  },
  {
    name: 'orders-create-calculator',
    path: '/create/calculator',
    meta: {
      step: 'calculator',
      title: 'Tarifberechnung',
      description: 'Hier können Sie für Ihren Auftrag einen Preis kalkulieren und mit dem derzeitigen Anbieter vergleichen.',
      mode: 'create',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      canAccessStep,
      hasFormData,
      hasCalculatorFormData,
      hasExistingCustomerData,
      hasCategories,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepCustomFields,
      hasFeatures,
      hasCompanySettings,
    ),
    component: () => import(/* webpackChunkName: "order-calculator" */ '@/apps/order/views/OrderCreateCalculatorView.vue'),
  },
  {
    name: 'orders-create-address',
    path: '/create/address',
    meta: {
      step: 'address',
      title: 'Adressdaten hinterlegen',
      description: 'Bitte füllen Sie die Adressdaten aus.',
      mode: 'create',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      canAccessStep,
      hasFormData,
      hasCalculatorFormData,
      hasCategories,
      hasExistingCustomerData,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepAddress,
      hasDataPreparedStepCustomFields,
      hasFeatures,
    ),
    component: () => import(/* webpackChunkName: "order-address" */ '@/apps/order/views/OrderCreateAddressView.vue'),
  },
  {
    name: 'orders-create-consumption',
    path: '/create/consumption-point',
    meta: {
      step: 'consumptionPoint',
      title: 'Anschlussdaten hinterlegen',
      description: 'Bitte füllen Sie die Anschlussdaten aus.',
      mode: 'create',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      canAccessStep,
      hasFormData,
      hasCalculatorFormData,
      hasCategories,
      hasExistingCustomerData,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepConsumptionPoint,
      hasDataPreparedStepCustomFields,
    ),
    component: () => import(/* webpackChunkName: "order-consumption-point" */ '@/apps/order/views/OrderCreateConsumptionPointView.vue'),
  },
  {
    name: 'orders-create-payment',
    path: '/create/payment',
    meta: {
      step: 'payment',
      title: 'Zahlungsweise hinterlegen',
      description: 'Bitte füllen Sie die Zahlungsdaten aus.',
      mode: 'create',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      canAccessStep,
      hasFormData,
      hasCalculatorFormData,
      hasCategories,
      hasExistingCustomerData,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepConsumptionPoint,
      hasDataPreparedStepPayment,
      hasDataPreparedStepCustomFields,
    ),
    component: () => import(/* webpackChunkName: "order-payment" */ '@/apps/order/views/OrderCreatePaymentView.vue'),
  },
  {
    name: 'orders-create-custom-fields',
    path: '/create/custom-fields',
    meta: {
      step: 'customFields',
      title: 'Weitere Angaben',
      description: 'Bitte füllen Sie die weiteren Angaben aus.',
      mode: 'create',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      canAccessStep,
      hasFormData,
      hasCalculatorFormData,
      hasCategories,
      hasExistingCustomerData,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepConsumptionPoint,
      hasDataPreparedStepPayment,
      hasDataPreparedStepCustomFields,
    ),
    component: () => import(/* webpackChunkName: "order-custom-fields" */ '@/apps/order/views/OrderCreateCustomFieldsView.vue'),
  },
  {
    name: 'orders-create-overview',
    path: '/create/overview',
    meta: {
      step: 'overview',
      title: 'Zusammenfassung',
      description: 'Bitte prüfen Sie alle eingegebenen Daten.',
      mode: 'create',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      canAccessStep,
      hasFormData,
      hasCalculatorFormData,
      hasPreparedData,
      hasCategories,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepAddress,
      hasDataPreparedStepPayment,
      hasDataPreparedStepCustomFields,
      hasDataPreparedStepConsumptionPoint,
      hasFeatures,
    ),
    component: () => import(/* webpackChunkName: "order-overview" */ '@/apps/order/views/OrderCreateOverviewView.vue'),
  },
];

const editRoutes: RouteConfig[] = [
  {
    name: 'orders-edit',
    path: '/:orderUuid/edit',
    meta: {
      step: 'address',
      title: 'Adressdaten hinterlegen',
      description: 'Bitte füllen Sie die Adressdaten aus.',
      mode: 'edit',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      clearCacheOnEnter,
      hasDataPreparedStepCustomFields,
    ),
    component: () => import(/* webpackChunkName: "order-edit" */ '@/apps/order/views/OrderEditView.vue'),
  },
  {
    name: 'orders-edit-address',
    path: '/:orderUuid/edit/address',
    meta: {
      step: 'address',
      title: 'Adressdaten hinterlegen',
      description: 'Bitte füllen Sie die Adressdaten aus.',
      mode: 'edit',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      canAccessStep,
      hasFormData,
      hasCalculatorFormData,
      hasCategories,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepAddress,
      hasDataPreparedStepCustomFields,
      hasFeatures,
    ),
    component: () => import(/* webpackChunkName: "order-address" */ '@/apps/order/views/OrderCreateAddressView.vue'),
  },
  {
    name: 'orders-edit-consumption',
    path: '/:orderUuid/edit/consumption-point',
    meta: {
      step: 'consumptionPoint',
      title: 'Anschlussdaten hinterlegen',
      description: 'Bitte füllen Sie die Anschlussdaten aus.',
      mode: 'edit',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      canAccessStep,
      hasCategories,
      hasFormData,
      hasCalculatorFormData,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepConsumptionPoint,
      hasDataPreparedStepCustomFields,
    ),
    component: () => import(/* webpackChunkName: "order-consumption-point" */ '@/apps/order/views/OrderCreateConsumptionPointView.vue'),
  },
  {
    name: 'orders-edit-payment',
    path: '/:orderUuid/edit/payment',
    meta: {
      step: 'payment',
      title: 'Zahlungsweise hinterlegen',
      description: 'Bitte füllen Sie die Zahlungsdaten aus.',
      mode: 'edit',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      canAccessStep,
      hasFormData,
      hasCalculatorFormData,
      hasCategories,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepConsumptionPoint,
      hasDataPreparedStepPayment,
      hasDataPreparedStepCustomFields,
    ),
    component: () => import(/* webpackChunkName: "order-payment" */ '@/apps/order/views/OrderCreatePaymentView.vue'),
  },
  {
    name: 'orders-edit-custom-fields',
    path: '/:orderUuid/edit/custom-fields',
    meta: {
      step: 'customFields',
      title: 'Benutzerdefinierte Felder',
      description: 'Bitte füllen Sie die benutzerdefinierten Felder aus.',
      mode: 'edit',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      canAccessStep,
      hasFormData,
      hasCalculatorFormData,
      hasCategories,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepConsumptionPoint,
      hasDataPreparedStepPayment,
      hasDataPreparedStepCustomFields,
    ),
    component: () => import(/* webpackChunkName: "order-custom-fields" */ '@/apps/order/views/OrderCreateCustomFieldsView.vue'),
  },
  {
    name: 'orders-edit-overview',
    path: '/:orderUuid/edit/overview',
    meta: {
      step: 'overview',
      title: 'Zusammenfassung',
      description: 'Bitte prüfen Sie alle eingegebenen Daten.',
      mode: 'edit',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      canAccessStep,
      hasFormData,
      hasCalculatorFormData,
      hasPreparedData,
      hasCategories,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepAddress,
      hasDataPreparedStepPayment,
      hasDataPreparedStepCustomFields,
      hasDataPreparedStepConsumptionPoint,
      hasFeatures,
    ),
    component: () => import(/* webpackChunkName: "order-overview" */ '@/apps/order/views/OrderCreateOverviewView.vue'),
  },
];

const tariffChangeRoutes: RouteConfig[] = [
  {
    name: 'orders-tariff-change',
    path: '/:contractUuid/order/tariff-change',
    meta: {
      step: 'calculator',
      title: 'Tarifwechsel',
      description: 'Wählen Sie einen neuen Tarif aus.',
      mode: 'tariff-change',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      clearCacheOnEnter,
    ),
    component: () => import(/* webpackChunkName: "tariff-change" */ '@/apps/order/views/TariffChangeView.vue'),
  },
  {
    name: 'orders-tariff-change-calculator',
    path: '/:contractUuid/order/tariff-change/calculator',
    meta: {
      step: 'calculator',
      title: 'Tarifberechnung',
      description: $text('order_process', 'tariffChange.introduction', 'Hier können Sie einen neuen Tarif auswählen.'),
      mode: 'tariff-change',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      ensureTariffChangeData,
      hasFormData,
      hasCalculatorFormData,
      hasCategories,
      hasDataPreparedStepCalculator,
      hasFeatures,
      hasCompanySettings,
    ),
    component: () => import(/* webpackChunkName: "order-calculator" */ '@/apps/order/views/OrderCreateCalculatorView.vue'),
  },
  {
    // Internet tariff-change consumption step: commodity skips to overview; internet completes Fiber cascade here.
    name: 'orders-tariff-change-consumption',
    path: '/:contractUuid/order/tariff-change/consumption-point',
    meta: {
      step: 'consumptionPoint',
      title: 'Anschlussdaten hinterlegen',
      description: 'Bitte füllen Sie die Anschlussdaten aus.',
      mode: 'tariff-change',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      ensureTariffChangeData,
      canAccessStep,
      hasFormData,
      hasCalculatorFormData,
      hasCategories,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepConsumptionPoint,
    ),
    component: () => import(/* webpackChunkName: "order-consumption-point" */ '@/apps/order/views/OrderCreateConsumptionPointView.vue'),
  },
  {
    name: 'orders-tariff-change-overview',
    path: '/:contractUuid/order/tariff-change/overview',
    meta: {
      step: 'overview',
      title: 'Zusammenfassung',
      description: 'Bitte prüfen Sie alle eingegebenen Daten.',
      mode: 'tariff-change',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      ensureTariffChangeData,
      canAccessStep,
      hasFormData,
      hasCalculatorFormData,
      hasPreparedData,
      hasCategories,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepAddress,
      hasDataPreparedStepPayment,
      hasDataPreparedStepConsumptionPoint,
      hasFeatures,
    ),
    component: () => import(/* webpackChunkName: "order-overview" */ '@/apps/order/views/OrderCreateOverviewView.vue'),
  },
];

const relocationRoutes: RouteConfig[] = [
  {
    name: 'orders-relocation',
    path: '/:contractUuid/order/relocation',
    meta: {
      step: 'address',
      title: 'Umzug',
      description: 'Bitte füllen Sie die Umzugdaten aus.',
      mode: 'relocation',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      clearCacheOnEnter,
      hasDataPreparedStepCustomFields,
    ),
    component: () => import(/* webpackChunkName: "relocation" */ '@/apps/order/views/RelocationView.vue'),
  },
  {
    name: 'orders-relocation-address',
    path: '/:contractUuid/order/relocation/address',
    meta: {
      step: 'address',
      title: 'Adressdaten hinterlegen',
      description: $text('order_process', 'relocation.addressNote', 'Bitte füllen Sie die Adressdaten aus.'),
      mode: 'relocation',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      ensureRelocationData,
      canAccessStep,
      hasFormData,
      hasCalculatorFormData,
      hasCategories,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepAddress,
      hasDataPreparedStepCustomFields,
      hasFeatures,
      hasRelocationSelectedTariffs,
    ),
    component: () => import(/* webpackChunkName: "order-address" */ '@/apps/order/views/OrderCreateAddressView.vue'),
  },
  {
    name: 'orders-relocation-consumption',
    path: '/:contractUuid/order/relocation/consumption-point',
    meta: {
      step: 'consumptionPoint',
      title: 'Anschlussdaten hinterlegen',
      description: $text('order_process', 'relocation.connectionDataNote', 'Bitte füllen Sie die Anschlussdaten aus.'),
      mode: 'relocation',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      ensureRelocationData,
      canAccessStep,
      hasFormData,
      hasCalculatorFormData,
      hasCategories,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepConsumptionPoint,
      hasDataPreparedStepCustomFields,
      hasRelocationSelectedTariffs,
    ),
    component: () => import(/* webpackChunkName: "order-consumption-point" */ '@/apps/order/views/OrderCreateConsumptionPointView.vue'),
  },
  {
    name: 'orders-relocation-payment',
    path: '/:contractUuid/order/relocation/payment',
    meta: {
      step: 'payment',
      title: 'Zahlungsweise hinterlegen',
      description: 'Bitte füllen Sie die Zahlungsdaten aus.',
      mode: 'relocation',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      ensureRelocationData,
      canAccessStep,
      hasFormData,
      hasCalculatorFormData,
      hasCategories,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepConsumptionPoint,
      hasDataPreparedStepPayment,
      hasDataPreparedStepCustomFields,
      hasRelocationSelectedTariffs,
    ),
    component: () => import(/* webpackChunkName: "order-payment" */ '@/apps/order/views/OrderCreatePaymentView.vue'),
  },
  {
    name: 'orders-relocation-overview',
    path: '/:contractUuid/order/relocation/overview',
    meta: {
      step: 'overview',
      title: 'Zusammenfassung',
      description: $text('order_process', 'relocation.summaryNote', 'Bitte prüfen Sie alle eingegebenen Daten.'),
      mode: 'relocation',
      ...sharedMeta,
    },
    beforeEnter: (...routerParams: any[]) => runGuards(
      ...routerParams,
      ensureRelocationData,
      canAccessStep,
      hasFormData,
      hasCalculatorFormData,
      hasPreparedData,
      hasCategories,
      hasDataPreparedStepCalculator,
      hasDataPreparedStepAddress,
      hasDataPreparedStepPayment,
      hasDataPreparedStepCustomFields,
      hasDataPreparedStepConsumptionPoint,
      hasFeatures,
      hasRelocationSelectedTariffs,
    ),
    component: () => import(/* webpackChunkName: "order-overview" */ '@/apps/order/views/OrderCreateOverviewView.vue'),
  },
];

const ordersCreateRouter = new VueRouter({
  mode: 'history',
  base: BASE_PATH,
  routes: [
    ...routes,
    ...editRoutes,
    ...tariffChangeRoutes,
    ...relocationRoutes,
  ],
});

// Store the original push method
const originalPush = ordersCreateRouter.push.bind(ordersCreateRouter);

// Override the push method safely
ordersCreateRouter.push = (location: RawLocation) => {
  Meta.clear(ordersCreateRouter.currentRoute as Route);
  return originalPush(location);
};

ordersCreateRouter.beforeEach(async (to, from, next) => {
  const queryMode = to.query.source as string | undefined;
  let currentOrderMode: string | undefined = ORDER_MODE;

  if (queryMode) {
    currentOrderMode = queryMode;
    storeInSessionStorage(`${CONTEXT}.orderMode`, queryMode);
  } else if (to.meta?.mode === 'relocation') {
    // Relocation-step reload: the query param is gone, so ORDER_MODE holds the blade's
    // 'tariffChange' default. Trust the route and the persisted mode instead.
    currentOrderMode = getFromSessionStorage<string>(`${CONTEXT}.orderMode`) || 'relocation';
  } else if (!currentOrderMode) {
    currentOrderMode = getFromSessionStorage<string>(`${CONTEXT}.orderMode`);
  }

  const contractData = (window as unknown as { CONTRACT_DATA?: Record<string, any> }).CONTRACT_DATA;
  // An empty server payload arrives as `[]`, which is truthy in JS — writing that here
  // wiped the relocation's own recovery copy on every reload.
  if (hasPayload(contractData) && currentOrderMode === 'tariffChange') {
    storeInSessionStorage(`${CONTEXT}.contractData`, contractData);
  }

  (window.ROUTER as any) = {
    to,
    from,
    meta: {
      ...to.meta,
    },
    basePath: BASE_PATH,
    orderMode: currentOrderMode || undefined,
  };
  next();
});

// io.analytics-Pageviews: Der Bestellprozess ist eine SPA (history mode) — jeder Schrittwechsel
// ändert die URL ohne echten Seiten-Load, den das Tracking sonst verpasst. v1.js (im Layout)
// trackt die EINSTIEGSseite selbst, daher melden wir hier nur die FOLGE-Navigationen. Das
// Facade-trackPageView() setzt URL + Titel selbst; ist v1.js noch nicht geladen, geht der Aufruf
// in die Warteschlange und wird nach dem Laden abgespielt.
let ordersCreateLastTrackedPath: string | null = null;
ordersCreateRouter.afterEach((to) => {
  if (ordersCreateLastTrackedPath === null) {
    ordersCreateLastTrackedPath = to.fullPath; // Einstiegsseite → v1.js, nicht doppelt zählen
    return;
  }
  // gleiche URL (z. B. durch einen Redirect) nicht doppelt melden
  if (to.fullPath === ordersCreateLastTrackedPath) return;
  ordersCreateLastTrackedPath = to.fullPath;

  const io = (window as unknown as {
    ioAnalytics?: { trackPageView?: () => void, q?: unknown[] }
  }).ioAnalytics;
  if (!io) return;
  // A throwing hook would skip the remaining afterEach hooks (funnel below) — best-effort only.
  try {
    if (typeof io.trackPageView === 'function') io.trackPageView();
    else if (Array.isArray(io.q)) io.q.push(['trackPageView']);
  } catch (e) {
    // tracking is best-effort
  }
});

// Funnel (order.create): the steps in wizard order. Not every journey shows all of them —
// tariff change starts at the calculator, relocation at the address.
const WIZARD_STEPS = ['type', 'customer', 'category', 'calculator', 'address', 'consumptionPoint', 'payment', 'customFields', 'overview'];

// `wizard_started` once per wizard load (name = entry step), `wizard_step_advanced` each time a
// LATER step is reached (name = that step). Reported here and not on the „Weiter" click: the
// click may still be stopped by validation, a reached route is a fact. Going back is not
// reported. A reload mid-journey starts the wizard again — the funnel is a rough count of
// events, not of people (no sessions), and says so.
ordersCreateRouter.afterEach((to, from) => {
  const toStep = to.meta?.step as string | undefined;
  if (!toStep) return;

  if (!from.name) {
    orderTrack('wizard_started', toStep);
    return;
  }

  const fromIndex = WIZARD_STEPS.indexOf(from.meta?.step as string);
  if (WIZARD_STEPS.indexOf(toStep) > fromIndex) orderTrack('wizard_step_advanced', toStep);
});

export default ordersCreateRouter;
export { getOrderMode, CONTEXT };
